// Ported from v3 `src/utils/intercom.js` (`initIntercom`/`bootIntercom`/`shutdownIntercom`).
// Lives in `shared/lib` (not `app/providers`, unlike Sentry) because `features/auth`'s
// `AuthLogoutFlow` needs a real default teardown without importing `app/` — the same reason
// `gtm.ts` sits here. The env gate (`INTERCOM_APP_ID`) lives at the composition-root call sites
// (`app/bootstrap.ts`), not in this file, mirroring `pushToDataLayer`'s split.
//
// No hardcoded app id: v3's literal `'dv575d96'` is a banned string here (shell ledger §C defect
// 7, `app/shell/__tests__/ledgerC.invariants.spec.ts`) — every call carries the id through from
// `env.INTERCOM_APP_ID`, so an unconfigured environment loads nothing at all.

/** The subset of the Intercom JS API + queue shim this module drives. */
interface IntercomFunction {
  (...args: unknown[]): void
  q?: unknown[]
  c?: (args: IArguments) => void
}

interface IntercomWindow {
  Intercom?: IntercomFunction
  intercomSettings?: Record<string, unknown>
}

function intercomWindow(): IntercomWindow {
  return window as unknown as IntercomWindow
}

let installedAppId: string | null = null

/** Test-only: module state otherwise survives across specs in the same file (module caching). */
export function resetIntercomForTest(): void {
  installedAppId = null
}

/** Queue-shim + widget script load (v3 parity, minus the dead IE `attachEvent` branch). Safe to
 *  call more than once — a second call just re-attaches/updates the existing widget. */
export function installIntercom(appId: string | undefined): void {
  if (!appId) return
  installedAppId = appId
  const w = intercomWindow()
  w.intercomSettings = { api_base: 'https://api-iam.intercom.io', app_id: appId }

  if (typeof w.Intercom === 'function') {
    w.Intercom('reattach_activator')
    w.Intercom('update', w.intercomSettings)
    return
  }

  const stub: IntercomFunction = (...args: unknown[]) => stub.c?.(args as unknown as IArguments)
  stub.q = []
  stub.c = (args) => stub.q?.push(args)
  w.Intercom = stub

  const load = (): void => {
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    script.src = `https://widget.intercom.io/widget/${appId}`
    const first = document.querySelector('script')
    if (first?.parentNode) first.parentNode.insertBefore(script, first)
    else document.head.appendChild(script)
  }
  if (document.readyState === 'complete') load()
  else window.addEventListener('load', load)
}

export interface IntercomCompany {
  id: string
  name?: string | undefined
  plan?: string | undefined
  size?: number | undefined
}

export interface IntercomIdentity {
  userId?: string | undefined
  email?: string | undefined
  name?: string | undefined
  /** Identity Verification JWT the backend issues alongside the user payload. */
  userJwt?: string | undefined
  company?: IntercomCompany | undefined
}

/** Push/refresh the current visitor's identity (v3 `bootIntercom`). No-op until installed. */
export function identifyIntercom(identity: IntercomIdentity): void {
  const w = intercomWindow()
  if (!installedAppId || typeof w.Intercom !== 'function') return

  const data: Record<string, unknown> = { app_id: installedAppId }
  if (identity.userId) data.user_id = identity.userId
  if (identity.email) data.email = identity.email
  if (identity.name) data.name = identity.name
  if (identity.userJwt) data.intercom_user_jwt = identity.userJwt
  if (identity.company) {
    data.company = {
      company_id: identity.company.id,
      name: identity.company.name,
      plan: identity.company.plan,
      size: identity.company.size,
    }
  }
  w.Intercom('update', data)
}

/** Clear the widget's identity (v3 `shutdownIntercom`) — logout, cross-tab reset. */
export function shutdownIntercom(): void {
  const intercom = intercomWindow().Intercom
  if (typeof intercom === 'function') intercom('shutdown')
}

/** Open the messenger programmatically (credits "chat with sales" CTAs). */
export function openIntercomChat(): boolean {
  const intercom = intercomWindow().Intercom
  if (typeof intercom !== 'function') return false
  intercom('show')
  return true
}
